import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { getGroupLink } from '~/utils/getLinks';
import { EditGroupFormFieldValues, editGroupSchema } from '../logics/schema';
import { EditGroupFormPresentation } from '../presentations';
import { useGetPartnerAdminsGroup } from './useGetPartnerAdminsGroup';
import { usePutPartnerAdminsGroup } from './usePutPartnerAdminsGroup';

type PresentationProps = React.ComponentProps<typeof EditGroupFormPresentation>;

type IUseEditGroupForm = (groupId: number) => {
  state: {
    methods: UseFormReturn<EditGroupFormFieldValues>;
    isMutating: boolean;
    groupName: string;
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * グループ編集フォームのカスタムフック
 *
 * @param groupId - 編集対象のグループID
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isMutating - API実行中フラグ
 * @returns state.groupName - グループ名（パンくず表示用）
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 * @returns actions.handleOnCancel - キャンセルハンドラ
 */
export const useEditGroupForm: IUseEditGroupForm = (groupId: number) => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data } = useGetPartnerAdminsGroup({
    threeLetterCode,
    groupId,
  });

  const methods = useForm<EditGroupFormFieldValues>({
    defaultValues: {
      name: data.group.name ?? '',
      groupId: data.group.code ?? '',
      description: data.group.description ?? '',
    },
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(editGroupSchema),
  });

  const { trigger, isMutating } = usePutPartnerAdminsGroup(threeLetterCode, groupId, {
    onSuccess: ({ group: { name } }) => {
      showToast({
        message: `「${name}」を保存しました。`,
        status: 'success',
      });
      navigate(getGroupLink(groupId));
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'グループの更新に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: 'グループの保存に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(async ({ name, groupId, description }) => {
    await trigger({
      group: {
        name,
        code: groupId,
        description: description ?? '',
      },
    });
  });

  return {
    state: {
      methods,
      isMutating,
      groupName: data.group.name ?? '',
    },
    actions: { handleOnSubmit },
  };
};
